import { Card, Col, Row, Tab } from 'react-bootstrap';
import Flex from '@/components/common/Flex';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import ClusterViewModeSwitcher from '@/app/KeywordResearch/components/keyword-clustring/components/ClusterViewModeSwitcher';
import { DensityTabContent } from './DensityTabs';
import {
  headers,
  images,
  links,
  metas
} from '@/app/PageSpeed/config/internalSeoColumnConfig';
import { colorByIndex, getColor } from '@/helpers/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, type ComponentType, type ReactNode } from 'react';
import type {
  AnalysisPageContent,
  DensityStats,
  HeaderStats,
  ImageStats,
  LinkStats,
  MetaStats
} from '@/app/PageSpeed/api/types/analysisApiTypes';

type AgGridWrapperProps = {
  height?: string;
  columns?: unknown[];
  data?: unknown[];
};
const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;

type ItemListProps = {
  item: { name: string; value?: number; color: string };
  active?: boolean;
  onClick?: () => void;
};
type ContentTabsProps = {
  contentData?: AnalysisPageContent | null;
  headerStats?: HeaderStats | null;
  imageStats?: ImageStats | null;
  metaStats?: MetaStats | null;
  linkStats?: LinkStats | null;
  densityStats?: DensityStats | null;
};

const ItemList = ({ item, active, onClick }: ItemListProps) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`border-0 bg-transparent p-0 ${
        active ? 'text-primary' : 'text-900'
      }`}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
      aria-pressed={onClick ? active : undefined}
    >
      <Flex
        key={item.name}
        alignItems="center"
        justifyContent="start"
        className={`fw-semi-bold text-nowrap text-capitalize fs--1 gap-1 `}
      >
        <FontAwesomeIcon icon="circle" color={getColor(item.color)} />
        {item.name === 'total' ? 'همه' : item.name}:
        <div className="fw-black">
          {typeof item.value === 'number'
            ? item.value.toLocaleString('fa-IR')
            : '۰'}
        </div>
      </Flex>
    </button>
  );
};

const ContentTabs = ({
  contentData,
  headerStats,
  imageStats,
  metaStats,
  linkStats,
  densityStats
}: ContentTabsProps) => {
  const [activeTab, setActiveTab] = useState('2');
  const [activeFilters, setActiveFilters] = useState<
    Record<number, string | undefined>
  >({});
  const content = contentData || {};
  const headersStatsSafe = headerStats || {};
  const imageStatsSafe = imageStats || {};
  const metaStatsSafe = metaStats || {};
  const linkStatsSafe = linkStats || {};
  const headerCounts = (content.headers || []).reduce<Record<string, number>>(
    (counts, header) => {
      const tagName = String(
        (header as { tagName?: string }).tagName || ''
      ).toLowerCase();
      if (tagName) counts[tagName] = (counts[tagName] || 0) + 1;
      return counts;
    },
    {}
  );
  const linkRows = content.links || [];
  const linkCounts = {
    unique: linkRows.filter(link => Boolean(link.isUnique)).length,
    internal: linkRows.filter(link => Boolean(link.isInternal)).length,
    external: linkRows.filter(link => !link.isInternal).length,
    https: linkRows.filter(link => Boolean(link.isHttps)).length,
    http: linkRows.filter(link => !link.isHttps).length,
    follow: linkRows.filter(link => {
      const rel = String(link.rel || '').toLowerCase();
      return link.isFollow === true || (!rel && link.isNofollow !== true);
    }).length,
    nofollow: linkRows.filter(link => {
      const rel = String(link.rel || '').toLowerCase();
      return link.isNofollow === true || rel.includes('nofollow');
    }).length,
    total: linkRows.length,
    repeated: linkRows.reduce<Record<string, number>>((counts, link) => {
      const occurrenceCount = Number(link.occurrenceCount);
      if (occurrenceCount) {
        counts[String(occurrenceCount)] =
          (counts[String(occurrenceCount)] || 0) + 1;
      }
      return counts;
    }, {})
  };
  const imageRows = content.images || [];
  const imageCounts = {
    withAlt: imageRows.filter(image => Boolean(String(image.alt || '').trim()))
      .length,
    withoutAlt: imageRows.filter(image => !String(image.alt || '').trim())
      .length,
    withTitle: imageRows.filter(image =>
      Boolean(String(image.title || '').trim())
    ).length,
    withoutTitle: imageRows.filter(image => !String(image.title || '').trim())
      .length,
    total: imageRows.length
  };
  const metaRows = content.metas || [];
  const metaCounts = metaRows.reduce<Record<string, number>>((counts, meta) => {
    const mode = String(meta.mode || 'other').toLowerCase();
    const normalizedMode = ['og', 'twitter', 'link'].includes(mode)
      ? mode
      : 'other';
    counts[normalizedMode] = (counts[normalizedMode] || 0) + 1;
    return counts;
  }, {});
  metaCounts.total = metaRows.length;
  const toggleFilter = (tabId: number, filter?: string) => {
    setActiveFilters(current => ({
      ...current,
      [tabId]: current[tabId] === filter ? undefined : filter
    }));
  };
  const filterData = (tabId: number, data: unknown[]) => {
    const filter = activeFilters[tabId];
    if (!filter) return data;

    return data.filter(row => {
      const item = row as Record<string, unknown>;
      switch (tabId) {
        case 2:
          return String(item.tagName || '').toLowerCase() === filter;
        case 3:
          if (filter === 'unique') return item.isUnique === true;
          if (filter === 'internal') return item.isInternal === true;
          if (filter === 'external') return item.isInternal === false;
          if (filter === 'https') return item.isHttps === true;
          if (filter === 'http') return item.isHttps === false;
          if (filter === 'follow')
            return (
              item.isFollow === true || (!item.rel && item.isNofollow !== true)
            );
          if (filter === 'nofollow')
            return (
              item.isNofollow === true ||
              String(item.rel || '')
                .toLowerCase()
                .includes('nofollow')
            );
          if (filter === 'more') return Number(item.occurrenceCount) > 5;
          return Number(item.occurrenceCount) === Number(filter);
        case 4:
          if (filter === 'withAlt')
            return Boolean(String(item.alt || '').trim());
          if (filter === 'withoutAlt') return !String(item.alt || '').trim();
          if (filter === 'withTitle')
            return Boolean(String(item.title || '').trim());
          return !String(item.title || '').trim();
        case 5: {
          const mode = String(item.mode || 'other').toLowerCase();
          return ['og', 'twitter', 'link'].includes(mode)
            ? mode === filter
            : filter === 'other';
        }
        default:
          return true;
      }
    });
  };

  const navItems: Array<{
    id: number;
    name: string;
    tooltip: string;
    columns: unknown[];
    data?: unknown[];
    header: ReactNode;
  }> = [
    {
      id: 2,
      name: 'هدینگ ها',
      tooltip: '',
      columns: headers,
      data: content.headers || [],
      header: (
        <>
          <div className="mb-3">
            <Flex
              className="gap-3 py-1 overflow-x-auto"
              wrap="nowrap"
              alignItems="center"
            >
              {['all', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].map((tag, i) => (
                <ItemList
                  key={tag}
                  item={{
                    name: tag === 'all' ? 'همه' : tag,
                    value:
                      tag === 'all'
                        ? Object.values(headerCounts).reduce(
                            (sum, count) => sum + count,
                            0
                          )
                        : Math.max(
                            headersStatsSafe[tag] ??
                              headersStatsSafe[tag.toUpperCase()] ??
                              0,
                            headerCounts[tag] || 0
                          ),
                    color: colorByIndex(i)
                  }}
                  active={
                    tag === 'all' ? !activeFilters[2] : activeFilters[2] === tag
                  }
                  onClick={() =>
                    toggleFilter(2, tag === 'all' ? undefined : tag)
                  }
                />
              ))}
            </Flex>
          </div>
        </>
      )
    },
    {
      id: 3,
      name: 'پیوندها',
      tooltip: '',
      columns: links,
      data: content.links || [],
      header: (
        <div className="mb-3">
          <Flex
            className="gap-3 py-1 overflow-x-auto"
            wrap="nowrap"
            alignItems="center"
          >
            {[
              {
                name: 'همه',
                filter: undefined,
                value: Math.max(
                  linkStatsSafe.rel?.total || 0,
                  linkCounts.total
                ),
                color: 'primary'
              },
              {
                name: 'یکتا',
                filter: 'unique',
                value: Math.max(
                  linkStatsSafe.mode?.unique || 0,
                  linkCounts.unique
                ),
                color: 'primary'
              },
              {
                name: 'داخلی',
                filter: 'internal',
                value: Math.max(
                  linkStatsSafe.mode?.internal || 0,
                  linkCounts.internal
                ),
                color: 'ourcolor-1'
              },
              {
                name: 'خارجی',
                filter: 'external',
                value: Math.max(
                  linkStatsSafe.mode?.external || 0,
                  linkCounts.external
                ),
                color: 'ourcolor-2'
              },
              {
                name: 'https',
                filter: 'https',
                value: Math.max(
                  linkStatsSafe.protocol?.https || 0,
                  linkCounts.https
                ),
                color: 'success'
              },
              {
                name: 'http',
                filter: 'http',
                value: Math.max(
                  linkStatsSafe.protocol?.http || 0,
                  linkCounts.http
                ),
                color: 'danger'
              },
              {
                name: 'فالو',
                filter: 'follow',
                value: Math.max(
                  linkStatsSafe.rel?.follow || 0,
                  linkCounts.follow
                ),
                color: 'info'
              },
              {
                name: 'نوفالو',
                filter: 'nofollow',
                value: Math.max(
                  linkStatsSafe.rel?.nofollow || 0,
                  linkCounts.nofollow
                ),
                color: 'info'
              },
              {
                name: '۲ بار تکرار',
                filter: '2',
                value: Math.max(
                  linkStatsSafe.count_links?.['2'] || 0,
                  linkCounts.repeated['2'] || 0
                ),
                color: 'secondary'
              },
              {
                name: '۳ بار تکرار',
                filter: '3',
                value: Math.max(
                  linkStatsSafe.count_links?.['3'] || 0,
                  linkCounts.repeated['3'] || 0
                ),
                color: 'secondary'
              },
              {
                name: '۴ بار تکرار',
                filter: '4',
                value: Math.max(
                  linkStatsSafe.count_links?.['4'] || 0,
                  linkCounts.repeated['4'] || 0
                ),
                color: 'secondary'
              },
              {
                name: '۵ بار تکرار',
                filter: '5',
                value: Math.max(
                  linkStatsSafe.count_links?.['5'] || 0,
                  linkCounts.repeated['5'] || 0
                ),
                color: 'secondary'
              },
              {
                name: 'بالای ۵ بار تکرار',
                filter: 'more',
                value: Math.max(
                  linkStatsSafe.count_links?.more || 0,
                  Object.entries(linkCounts.repeated)
                    .filter(([count]) => Number(count) > 5)
                    .reduce((sum, [, value]) => sum + value, 0)
                ),
                color: 'secondary'
              }
            ].map(({ name, filter, value, color }) => (
              <ItemList
                key={name}
                item={{
                  name,
                  value,
                  color
                }}
                active={
                  !filter ? !activeFilters[3] : activeFilters[3] === filter
                }
                onClick={() => toggleFilter(3, filter)}
              />
            ))}
          </Flex>
        </div>
      )
    },
    {
      id: 4,
      name: 'تصاویر',
      tooltip: '',
      columns: images,
      data: content.images || [],
      header: (
        <div className="mb-3">
          <Flex
            className="gap-3 py-1 overflow-x-auto"
            wrap="nowrap"
            alignItems="center"
          >
            {[
              {
                name: 'همه',
                filter: undefined,
                value: Math.max(
                  imageStatsSafe.alt?.total || 0,
                  imageCounts.total
                ),
                color: 'primary'
              },
              {
                name: 'دارای متن‌جایگزین',
                filter: 'withAlt',
                value: Math.max(
                  imageStatsSafe.alt?.with_alt || 0,
                  imageCounts.withAlt
                ),
                color: 'success'
              },
              {
                name: 'بدون متن‌جایگزین',
                filter: 'withoutAlt',
                value: Math.max(
                  imageStatsSafe.alt?.without_alt || 0,
                  imageCounts.withoutAlt
                ),
                color: 'danger'
              },
              {
                name: 'دارای عنوان',
                filter: 'withTitle',
                value: Math.max(
                  imageStatsSafe.title?.with_title || 0,
                  imageCounts.withTitle
                ),
                color: 'success'
              },
              {
                name: 'بدون عنوان',
                filter: 'withoutTitle',
                value: Math.max(
                  imageStatsSafe.title?.without_title || 0,
                  imageCounts.withoutTitle
                ),
                color: 'warning'
              }
            ].map(({ name, filter, value, color }) => (
              <ItemList
                key={name}
                item={{
                  name,
                  value,
                  color
                }}
                active={
                  !filter ? !activeFilters[4] : activeFilters[4] === filter
                }
                onClick={() => toggleFilter(4, filter)}
              />
            ))}
          </Flex>
        </div>
      )
    },
    {
      id: 5,
      name: 'متاتگ‌ها',
      tooltip: '',
      columns: metas,
      data: content.metas || [],
      header: (
        <>
          <div className="mb-3">
            <Flex
              className="gap-3 py-1 overflow-x-auto"
              wrap="nowrap"
              alignItems="center"
            >
              {['total', 'og', 'twitter', 'link', 'other'].map((mode, i) => (
                <ItemList
                  key={mode}
                  item={{
                    name: mode,
                    value: Math.max(
                      metaStatsSafe[mode] || 0,
                      metaCounts[mode] || 0
                    ),
                    color: colorByIndex(i)
                  }}
                  active={
                    mode === 'total'
                      ? !activeFilters[5]
                      : activeFilters[5] === mode
                  }
                  onClick={() =>
                    toggleFilter(5, mode === 'total' ? undefined : mode)
                  }
                />
              ))}
            </Flex>
          </div>
        </>
      )
    },
    {
      id: 6,
      name: 'چگالی کلمات',
      tooltip: '',
      columns: [],
      header: (
        <DensityTabContent
          contentData={contentData}
          densityStats={densityStats}
        />
      )
    }
  ];

  return (
    <>
      <div className="my-3 d-flex w-100 justify-content-center">
        <ClusterViewModeSwitcher
          value={activeTab}
          onChange={setActiveTab}
          aria-label="نوع اطلاعات سئو"
          modes={[
            { id: '2', icon: 'text-paragraph', label: 'هدینگ ها' },
            { id: '3', icon: 'signpost', label: 'پیوندها' },
            { id: '4', icon: 'image', label: 'تصاویر' },
            { id: '5', icon: 'tags', label: 'متاتگ‌ها' },
            { id: '6', icon: 'list-ul', label: 'چگالی کلمات' }
          ]}
        />
      </div>
      <Card className="mt-3">
        <Tab.Container
          id="audience-tab"
          activeKey={activeTab}
          onSelect={key => key && setActiveTab(String(key))}
        >
          <Card.Body>
            <Row>
              <Col xxl={12}>
                <Tab.Content>
                  {navItems.map(item => (
                    <Tab.Pane
                      key={item.id}
                      unmountOnExit
                      eventKey={String(item.id)}
                    >
                      {item.header}
                      {item.data && (
                        <TypedAgGridWrapper
                          height="746px"
                          columns={item.columns}
                          data={filterData(item.id, item.data)}
                        />
                      )}
                    </Tab.Pane>
                  ))}
                </Tab.Content>
              </Col>
            </Row>
          </Card.Body>
        </Tab.Container>
      </Card>
    </>
  );
};

export default ContentTabs;
